import React, { useState, useEffect, useCallback, useRef } from 'react';
import '../../styles/PageCommon.css';
import { ReactComponent as CloseIcon } from '../../assets/icons/x-icon.svg';
import { ReactComponent as CheckIcon } from '../../assets/icons/check-icon.svg';
import { ReactComponent as PlusIcon } from '../../assets/icons/plus-icon.svg';
import { ReactComponent as CalendarIcon } from '../../assets/icons/calendar-icon.svg';
import { ReactComponent as ArrowDownIcon } from '../../assets/icons/arrow-down-icon.svg';
import SegmentedControl from '../common/SegmentedControl/SegmentedControl';
import CalendarModal from '../common/CalendarModal/CalendarModal';
import CustomCheckbox from '../common/CustomCheckbox/CustomCheckbox';
import MultiSelectDropdown from '../common/MultiSelectDropdown';
import SelectDropdown from '../common/SelectDropdown';
import ExpandableList from '../common/ExpandableList/ExpandableList';
import EditableCell, { EditableCellActions } from '../common/EditableCell';
import { AccordionCheckboxSelector } from '../common/AccordionCheckboxSelector/AccordionCheckboxSelector';
import { reportsService } from '../../services/reportsService';
import { formatToReadableDate, isoToDmy, formatIntoIsoDate, parseDmyDate } from '../../utils/utils';
import LoadingAnimation from '../common/LoadingAnimation/LoadingAnimation';
import { NotificationContainer } from '../common/NotificationContainer';
import NotificationModal from '../common/NotificationModal';
import { parseApiError } from '../../utils/parseErrorMessage';
import './PromoCalendarModal.css';

// Editable new-row state. Dates are held as ISO ("YYYY-MM-DD") from the native
// date inputs and converted to "DD.MM.YYYY" on submit. Branches and SKU codes
// are arrays picked from the dropdowns.
const EMPTY_ROW = {
  promo_name: "",
  promo_channel: "",
  promo_branches: [],
  promo_sku_codes: [],
  promo_start_date: "",
  promo_end_date: "",
  fact_value: "",
  baseline_forecast_value: "",
  promo_effect: "",
  promo_plan_value: "",
  promo_is_active: true,
};

const toNumber = (value) => (value === "" || value === null ? 0 : Number(value) || 0);
const PREVIEW_DEBOUNCE_MS = 150;
// Read-only fields the preview endpoint computes and fills in on the new-row
// form (also the values submitted on save).
const PREVIEW_RESULT_FIELDS = [
  "fact_value",
  "baseline_forecast_value",
  "promo_plan_value",
];
// Editing any of these re-requests the preview (debounced). `promo_effect` also
// triggers it, wired directly on its input.
const AUTO_PREVIEW_FIELDS = new Set([
  "promo_name",
  "promo_channel",
  "promo_branches",
  "promo_sku_codes",
  "promo_start_date",
  "promo_end_date",
  "promo_is_active",
]);

// Sentinel value for the "New" option in the existing-promos dropdown.
const NEW_PROMO_VALUE = "__new__";

// Promo endpoint dates are "DD.MM.YYYY"; the date inputs need ISO "YYYY-MM-DD".
const dmyToIso = (str) => {
  const d = parseDmyDate(str);
  return d ? formatIntoIsoDate(d) : "";
};

// Shared payload for the promo-activity endpoints: sent as query params by the
// GET preview and as the JSON body by create/update.
const buildPromoActivityPayload = (row = EMPTY_ROW) => ({
  promo_name: row.promo_name,
  promo_channel: row.promo_channel,
  promo_branches: row.promo_branches || [],
  promo_sku_codes: row.promo_sku_codes || [],
  promo_start_date: isoToDmy(row.promo_start_date),
  promo_end_date: isoToDmy(row.promo_end_date),
  promo_effect: toNumber(row.promo_effect),
  promo_is_active: !!row.promo_is_active,
});

// The preview endpoint needs branches, SKUs and both dates before it can
// compute anything, so it's only requested once all four are filled.
const hasPreviewRequiredFields = (row) =>
  !!(
    row?.promo_branches?.length &&
    row?.promo_sku_codes?.length &&
    row?.promo_start_date &&
    row?.promo_end_date
  );

const hydratePromoActivityRow = (promo, fallback = EMPTY_ROW) => ({
  ...fallback,
  promo_name: promo.promo_name ?? "",
  promo_channel: promo.promo_channel ?? "",
  promo_branches: promo.promo_branches || [],
  promo_sku_codes: promo.promo_sku_codes || [],
  promo_start_date: dmyToIso(promo.promo_start_date),
  promo_end_date: dmyToIso(promo.promo_end_date),
  fact_value: promo.fact_value ?? "",
  baseline_forecast_value: promo.baseline_forecast_value ?? "",
  promo_effect: promo.promo_effect ?? "",
  promo_plan_value: promo.promo_plan_value ?? "",
  promo_is_active: promo.promo_is_active ?? true,
});

const PromoCalendarModal = ({ closeModal, filters, updateFilters, productFiltersArray = [] }) => {
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [dataError, setDataError] = useState("");
  const [promoActivities, setPromoActivities] = useState([]);
  const [branchOptions, setBranchOptions] = useState([]);
  const [skuOptions, setSkuOptions] = useState([]);
  const [availablePromos, setAvailablePromos] = useState([]);

  const [newRow, setNewRow] = useState(null);
  const [editingPromoId, setEditingPromoId] = useState(null);
  const [editingRow, setEditingRow] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [selectedPromoId, setSelectedPromoId] = useState("");

  const [isProductsFilterOpen, setIsProductsFilterOpen] = useState(false);
  const [isCalendarFilterOpen, setIsCalendarFilterOpen] = useState(false);
  const previewDebounceRef = useRef(null);
  const previewRequestIdRef = useRef(0);

  const todayIso = formatIntoIsoDate(new Date());

  const clearPendingPreview = useCallback(() => {
    if (previewDebounceRef.current) {
      clearTimeout(previewDebounceRef.current);
      previewDebounceRef.current = null;
    }
  }, []);

  const requestPromoActivityPreview = useCallback((row, options = {}) => {
    if (!row) return;

    const { debounced = false, viewType = filters.view_type } = options;
    clearPendingPreview();

    // Nothing to compute until branches, SKUs and both dates are filled. Cancel
    // any in-flight request and clear stale computed values while incomplete.
    if (!hasPreviewRequiredFields(row)) {
      previewRequestIdRef.current += 1;
      setIsPreviewing(false);
      setNewRow((prev) => {
        if (!prev || PREVIEW_RESULT_FIELDS.every((field) => prev[field] === "")) return prev;
        const next = { ...prev };
        PREVIEW_RESULT_FIELDS.forEach((field) => { next[field] = ""; });
        return next;
      });
      return;
    }

    const requestId = previewRequestIdRef.current + 1;
    previewRequestIdRef.current = requestId;

    const runPreview = () => {
      previewDebounceRef.current = null;
      setIsPreviewing(true);

      reportsService.previewPromoActivity(viewType, buildPromoActivityPayload(row))
        .then((data) => {
          if (requestId !== previewRequestIdRef.current || !data) return;
          setNewRow((prev) => {
            if (!prev) return prev;
            const next = { ...prev };
            PREVIEW_RESULT_FIELDS.forEach((field) => {
              if (Object.prototype.hasOwnProperty.call(data, field)) {
                next[field] = data[field] ?? "";
              }
            });
            return next;
          });
        })
        .catch(error => {
          if (requestId === previewRequestIdRef.current) {
            setDataError(parseApiError(error) || "");
          }
        })
        .finally(() => {
          if (requestId === previewRequestIdRef.current) {
            setIsPreviewing(false);
          }
        });
    };

    if (debounced) {
      setIsPreviewing(true);
      previewDebounceRef.current = setTimeout(runPreview, PREVIEW_DEBOUNCE_MS);
      return;
    }

    runPreview();
  }, [clearPendingPreview, filters.view_type]);

  useEffect(() => () => {
    clearPendingPreview();
    previewRequestIdRef.current += 1;
  }, [clearPendingPreview]);

  const fetchActivities = useCallback(() => {
    setIsLoadingData(true);
    reportsService.getPromoActivities(filters)
      .then(data => {
        if (!data) return;
        setPromoActivities(Array.isArray(data.rows) ? data.rows : []);
        setBranchOptions(data.branch_options || []);
        setSkuOptions(data.sku_code_options || []);
        setAvailablePromos(Array.isArray(data.available_promos) ? data.available_promos : []);
      })
      .catch(error => setDataError(parseApiError(error) || ""))
      .finally(() => setIsLoadingData(false));
  }, [filters]);

  useEffect(() => {
    fetchActivities();
  }, [fetchActivities]);

  const openNewRow = () => {
    const row = { ...EMPTY_ROW };
    setEditingPromoId(null);
    setEditingRow(null);
    setFormErrors({});
    setSelectedPromoId(NEW_PROMO_VALUE);
    setNewRow(row);
    requestPromoActivityPreview(row);
  };

  const cancelNewRow = () => {
    clearPendingPreview();
    previewRequestIdRef.current += 1;
    setIsPreviewing(false);
    setFormErrors({});
    setSelectedPromoId("");
    setNewRow(null);
  };

  // Inline row editing: the draft lives in editingRow with ISO dates.
  const editActivity = (activity) => {
    const promoId = activity?.promo_id;
    if (promoId === null || promoId === undefined) return;

    cancelNewRow();
    setEditingPromoId(promoId);
    setEditingRow(hydratePromoActivityRow(activity, EMPTY_ROW));
  };

  const cancelRowEdit = () => {
    setEditingPromoId(null);
    setEditingRow(null);
  };

  const updateEditingRow = (field, value) => {
    setEditingRow((prev) => {
      if (!prev) return prev;
      const next = { ...prev, [field]: value };
      // Moving the start date past the end date drags the end date along with it.
      if (field === "promo_start_date" && next.promo_end_date && next.promo_end_date < value) {
        next.promo_end_date = value;
      }
      return next;
    });
  };

  const saveEditedRow = () => {
    if (!editingRow) return;
    const errors = validateNewRow(editingRow, { allowPastDates: true });
    const firstError = Object.values(errors).find(Boolean);
    if (firstError) {
      setDataError(firstError);
      return;
    }

    setIsSaving(true);
    reportsService.updatePromoActivity(editingPromoId, buildPromoActivityPayload(editingRow))
      .then(() => {
        cancelRowEdit();
        fetchActivities();
      })
      .catch(error => setDataError(parseApiError(error) || ""))
      .finally(() => setIsSaving(false));
  };

  // Prefill the form from an existing promo picked in the dropdown, or clear it
  // when "New" is picked.
  const fillFromPromo = (promoId) => {
    setSelectedPromoId(promoId);
    if (!promoId) return;
    if (promoId === NEW_PROMO_VALUE) {
      const row = { ...EMPTY_ROW };
      setFormErrors({});
      setNewRow(row);
      requestPromoActivityPreview(row);
      return;
    }
    const promo = availablePromos.find((p) => String(p.promo_id) === String(promoId));
    if (!promo) return;
    const row = hydratePromoActivityRow(promo, newRow || EMPTY_ROW);
    setFormErrors({});
    setNewRow(row);
    // Recompute live from the prefilled values instead of trusting the stored ones.
    requestPromoActivityPreview(row);
  };

  const updateNewRow = (field, value, options = {}) => {
    const row = { ...(newRow || EMPTY_ROW), [field]: value };
    // Moving the start date past the end date drags the end date along with it.
    if (field === "promo_start_date" && row.promo_end_date && row.promo_end_date < value) {
      row.promo_end_date = value;
    }
    setNewRow(row);
    // Clear a field's error as soon as the user edits it.
    setFormErrors((prev) => (prev[field] ? { ...prev, [field]: undefined } : prev));

    // Any edit re-requests the preview (debounced), regardless of whether the
    // row started blank or was prefilled from an existing promo.
    if (options.preview || AUTO_PREVIEW_FIELDS.has(field)) {
      requestPromoActivityPreview(row, { debounced: options.debounced ?? true });
    }
  };

  const handleViewTypeChange = (value) => {
    updateFilters({ view_type: value });
    if (newRow) {
      requestPromoActivityPreview(newRow, { viewType: value });
    }
  };

  // Validate required fields; returns an { field: message } map.
  // allowPastDates skips the "not before today" checks (used for inline edits
  // of promos that may have already started).
  const validateNewRow = (row, { allowPastDates = false } = {}) => {
    const errors = {};
    if (!row.promo_name?.trim()) errors.promo_name = "Введите название активности";
    if (!row.promo_branches?.length) errors.promo_branches = "Выберите хотя бы один склад";
    if (!row.promo_sku_codes?.length) errors.promo_sku_codes = "Выберите хотя бы один СКЮ";

    if (!row.promo_start_date) errors.promo_start_date = "Укажите дату начала";
    else if (!allowPastDates && row.promo_start_date < todayIso) errors.promo_start_date = "Не раньше сегодняшней";

    if (!row.promo_end_date) errors.promo_end_date = "Укажите дату окончания";
    else if (!allowPastDates && row.promo_end_date < todayIso) errors.promo_end_date = "Не раньше сегодняшней";
    else if (row.promo_start_date && row.promo_end_date < row.promo_start_date) errors.promo_end_date = "Не раньше даты начала";

    return errors;
  };

  const handleSave = () => {
    if (!newRow) {
      setDataError("Заполнено с ошибкой");
      return;
    }
    const errors = validateNewRow(newRow);
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setIsSaving(true);

    const body = {
      ...buildPromoActivityPayload(newRow),
      fact_value: toNumber(newRow.fact_value),
      baseline_forecast_value: toNumber(newRow.baseline_forecast_value),
      promo_plan_value: toNumber(newRow.promo_plan_value),
    };

    reportsService.createPromoActivity(filters.view_type, body)
      .then(() => {
        setNewRow(null);
        setSelectedPromoId("");
        setFormErrors({});
        fetchActivities();
      })
      .catch(error => setDataError(parseApiError(error) || ""))
      .finally(() => setIsSaving(false));
  };

  return (
    <div className="modal-overlay" onClick={() => closeModal()}>
      {(isLoadingData || isSaving) && <LoadingAnimation />}
      <NotificationContainer>
        {
          dataError && <NotificationModal closeModal={() => setDataError(null)} type="error" subTitle={dataError} title="Ошибка" duration={3000} />
        }
      </NotificationContainer>

      <div className="modal-container" style={{ width: "85%" }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header" style={{ borderBottom: "1px solid #E7E7E7", gap: "40px", paddingBottom: "12px", marginBottom: "10px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
            <h1>Промо-календарь</h1>
          </div>

          <button
            onClick={closeModal}
            className='modal-header-close-button'
            onMouseEnter={(e) => (e.currentTarget.style.color = "#4b5563")}
            onMouseLeave={(e) => (e.currentTarget.style.color = "#9ca3af")}
          >
            <CloseIcon size={24} />
          </button>
        </div>

        <div className='dp-chart-filters-container' style={{ gap: "12px" }}>
          <button
            onClick={() => setIsProductsFilterOpen(true)}
            className="filter-select"
          >
            <span className="filter-select-label">Продукты:</span>
            <span className="filter-select-option">
              {productFiltersArray.join(", ")}
            </span>
            {
              productFiltersArray.length > 0
              && <span style={{ background: "#7CC805", color: "#fff", borderRadius: 20, fontSize: 11, fontWeight: 700, padding: "1px 6px", flexShrink: 0 }}>
                {productFiltersArray.length}
              </span>
            }
            <ArrowDownIcon size={24} color='#7CC805' />
          </button>
          {
            isProductsFilterOpen &&
            <AccordionCheckboxSelector
              selectedOptions={(({ page, page_size, view_type, branch_name, promo_channel, date_to, date_from, ...rest }) => rest)(filters)}
              filterOptions={productFiltersArray}
              closeModal={() => setIsProductsFilterOpen(false)}
              onApply={(selectedFilter) => { updateFilters(selectedFilter); }}
            />
          }

          <SegmentedControl
            value={filters.view_type?.toLowerCase()}
            onChange={handleViewTypeChange}
            options={[
              {
                label: "Деньги",
                options: [
                  { label: "Прайс-лист", value: "dsp" },
                  { label: "Инвойс цена", value: "invoice price" },
                ],
              },
              { label: "Короб", value: "cases" },
              {
                label: "Вес",
                options: [
                  { label: "Брутто", value: "gross weight" },
                  { label: "Нетто", value: "net weight" },
                ],
              },
            ]}
          />

          <button
            onClick={() => setIsCalendarFilterOpen(true)}
            className="filter-select"
          >
            <CalendarIcon size={16} color='#7CC805' />
            <span className="filter-select-label">{formatToReadableDate(filters.date_from)} - {formatToReadableDate(filters.date_to)}</span>
            <ArrowDownIcon size={24} color='#7CC805' />
          </button>
          {
            isCalendarFilterOpen &&
            <CalendarModal title={"Выберите период"}
              isOpen={isCalendarFilterOpen}
              closeModal={() => setIsCalendarFilterOpen(false)}
              dateRange={{ startDate: filters.date_from, endDate: filters.date_to }}
              applyDateRangeFilter={(dateRange) => {
                updateFilters({ date_from: dateRange.startDate, date_to: dateRange.endDate });
              }}
            />
          }
          {
            !newRow && <div style={{ display: "flex", alignItems: "center", gap: "8px", marginLeft: 'auto' }}>
              <button type="button" className="secondary-button" onClick={openNewRow}>
                <PlusIcon width={20} height={20} />
                Добавить активность
              </button>
            </div>
          }
        </div>

        {/* <div className="promo-activity-modal-body" style={{ marginTop: "12px", overflow: "auto" }}> */}
        {
          newRow && (
            <form className="promo-activity-form" onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
              {
                availablePromos.length > 0 && (
                  <div className="promo-activity-form-field promo-activity-form-field--full">
                    <span>Заполнить из существующего промо</span>
                    <SelectDropdown
                      // label={"Промо"}
                      placeholder="Выберите промо"
                      filterSelectStyle={{ width: "100%" }}
                      value={selectedPromoId}
                      options={[
                        { value: NEW_PROMO_VALUE, label: "Новый" },
                        ...availablePromos.map((promo) => ({ value: promo.promo_id, label: promo.promo_name })),
                      ]}
                      onChange={(promoId) => fillFromPromo(promoId)}
                    />
                  </div>
                )
              }
              <label className={`promo-activity-form-field ${formErrors.promo_name ? 'promo-activity-form-field--error' : ''}`}>
                <span>Название активности</span>
                <input className="form-input" type="text"
                  value={newRow.promo_name}
                  onChange={(e) => updateNewRow("promo_name", e.target.value)} />
                {formErrors.promo_name && <span className="promo-activity-form-error">{formErrors.promo_name}</span>}
              </label>
              <label className="promo-activity-form-field">
                <span>Канал</span>
                <input className="form-input" type="text"
                  value={newRow.promo_channel}
                  onChange={(e) => updateNewRow("promo_channel", e.target.value)} />
              </label>
              <label className={`promo-activity-form-field ${formErrors.promo_branches ? 'promo-activity-form-field--error' : ''}`}>
                <span>Склады</span>
                <MultiSelectDropdown
                  label="Склады"
                  filterSelectStyle={{ width: "100%" }}
                  placeholder="Выберите"
                  options={branchOptions}
                  selectedOptions={newRow.promo_branches}
                  onChange={(values) => updateNewRow("promo_branches", values)}
                />
                {formErrors.promo_branches && <span className="promo-activity-form-error">{formErrors.promo_branches}</span>}
              </label>
              <label className={`promo-activity-form-field ${formErrors.promo_sku_codes ? 'promo-activity-form-field--error' : ''}`}>
                <span>СКЮ</span>
                <MultiSelectDropdown
                  label="СКЮ"
                  filterSelectStyle={{ width: "100%" }}
                  placeholder="Выберите"
                  options={skuOptions}
                  selectedOptions={newRow.promo_sku_codes}
                  onChange={(values) => updateNewRow("promo_sku_codes", values)}
                />
                {formErrors.promo_sku_codes && <span className="promo-activity-form-error">{formErrors.promo_sku_codes}</span>}
              </label>
              <label className={`promo-activity-form-field ${formErrors.promo_start_date ? 'promo-activity-form-field--error' : ''}`}>
                <span>Начало</span>
                <input className="form-input" type="date"
                  min={todayIso}
                  value={newRow.promo_start_date}
                  onChange={(e) => updateNewRow("promo_start_date", e.target.value)} />
                {formErrors.promo_start_date && <span className="promo-activity-form-error">{formErrors.promo_start_date}</span>}
              </label>
              <label className={`promo-activity-form-field ${formErrors.promo_end_date ? 'promo-activity-form-field--error' : ''}`}>
                <span>Конец</span>
                <input className="form-input" type="date"
                  min={newRow.promo_start_date || todayIso}
                  value={newRow.promo_end_date}
                  onChange={(e) => updateNewRow("promo_end_date", e.target.value)} />
                {formErrors.promo_end_date && <span className="promo-activity-form-error">{formErrors.promo_end_date}</span>}
              </label>
              <label className="promo-activity-form-field">
                <span>Факт прогноз</span>
                <input className="form-input form-input--readonly" type="number"
                  value={newRow.fact_value}
                  readOnly tabIndex={-1} />
              </label>
              <label className="promo-activity-form-field">
                <span>Базовый прогноз</span>
                <input className="form-input form-input--readonly" type="number"
                  value={newRow.baseline_forecast_value}
                  readOnly tabIndex={-1} />
              </label>
              <label className="promo-activity-form-field">
                <span>Эффект %</span>
                <input className="form-input" type="number"
                  value={newRow.promo_effect}
                  onChange={(e) => updateNewRow("promo_effect", e.target.value, { preview: true, debounced: true })} />
              </label>
              <label className="promo-activity-form-field">
                <span>Промо план</span>
                <input className="form-input form-input--readonly" type="number"
                  value={newRow.promo_plan_value}
                  readOnly tabIndex={-1} />
              </label>
              <div className="promo-activity-form-field promo-activity-form-field--checkbox">
                <span>Активен</span>
                <CustomCheckbox
                  checked={!!newRow.promo_is_active}
                  onChange={(e) => updateNewRow("promo_is_active", e.target.checked)}
                />
              </div>

              <div className="promo-activity-form-actions">
                <button type="button" className="cancel-button" onClick={cancelNewRow} disabled={isSaving}>
                  Отмена
                </button>
                <button type="submit" className="primary-button" disabled={isSaving || isPreviewing}>
                  <CheckIcon size={24} />
                  Сохранить
                </button>
              </div>
            </form>
          )
        }

        <div className="data-table-container custom-scrollbar" style={{ overflow: "auto", margin: "12px 0px" }}>
          <table className="data-table promo-activity-table">
            <thead>
              <tr>
                <th>Название активности</th>
                <th>Канал</th>
                <th>Склады</th>
                <th>СКЮ</th>
                <th>Начало</th>
                <th>Конец</th>
                <th>Факт прогноз</th>
                <th>Базовый прогноз</th>
                <th style={{ whiteSpace: "nowrap" }}>Эффект %</th>
                <th>Промо план</th>
                <th>Активен</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {
                promoActivities.length === 0 ? (
                  <tr>
                    <td colSpan={12} style={{ textAlign: "center", color: "#9a9a9a" }}>
                      Нет данных
                    </td>
                  </tr>
                ) : (
                  promoActivities.map((activity, index) => {
                    const isRowEditing = editingRow !== null && activity.promo_id === editingPromoId;
                    return (
                      <tr key={activity.promo_id ?? index}>
                        <td>
                          <EditableCell
                            type="text"
                            value={isRowEditing ? editingRow.promo_name : activity.promo_name}
                            isEditing={isRowEditing}
                            showControls={false}
                            editingContainerStyle={{ width: "100%", minWidth: "50px", marginLeft: "-8px", padding: "10px 8px" }}
                            onChange={(e) => updateEditingRow("promo_name", e.target.value)}
                          />
                        </td>
                        <td>
                          <EditableCell
                            type="text"
                            value={isRowEditing ? editingRow.promo_channel : activity.promo_channel}
                            isEditing={isRowEditing}
                            showControls={false}
                            editingContainerStyle={{ width: "100%", minWidth: "60px", marginLeft: "-8px", padding: "10px 8px" }}
                            onChange={(e) => updateEditingRow("promo_channel", e.target.value)}
                          />
                        </td>
                        <td>
                          {
                            isRowEditing
                              ? <MultiSelectDropdown
                                label=""
                                placeholder="Выберите"
                                options={branchOptions}
                                selectedOptions={editingRow.promo_branches}
                                onChange={(values) => updateEditingRow("promo_branches", values)}
                              />
                              : <ExpandableList options={branchOptions} selectedOptions={activity.promo_branches || []} />
                          }
                        </td>
                        <td>
                          {
                            isRowEditing
                              ? <MultiSelectDropdown
                                label=""
                                placeholder="Выберите"
                                options={skuOptions}
                                selectedOptions={editingRow.promo_sku_codes}
                                onChange={(values) => updateEditingRow("promo_sku_codes", values)}
                              />
                              : <ExpandableList options={skuOptions} selectedOptions={activity.promo_sku_codes || []} />
                          }
                        </td>
                        <td>
                          <EditableCell
                            editingContainerStyle={{ marginLeft: "-8px", padding: "10px 8px" }}
                            type="date"
                            value={isRowEditing ? editingRow.promo_start_date : activity.promo_start_date}
                            isEditing={isRowEditing}
                            showControls={false}
                            onChange={(e) => updateEditingRow("promo_start_date", e.target.value)}
                          />
                        </td>
                        <td>
                          <EditableCell
                            editingContainerStyle={{ marginLeft: "-8px", padding: "10px 8px" }}
                            type="date"
                            value={isRowEditing ? editingRow.promo_end_date : activity.promo_end_date}
                            isEditing={isRowEditing}
                            showControls={false}
                            onChange={(e) => updateEditingRow("promo_end_date", e.target.value)}
                            inputProps={{ min: editingRow?.promo_start_date || undefined }}
                          />
                        </td>
                        <td>{activity.fact_value?.toLocaleString("ru-RU")}</td>
                        <td>{activity.baseline_forecast_value?.toLocaleString("ru-RU")}</td>
                        <td>
                          <EditableCell
                            editingContainerStyle={{ marginLeft: "-8px", minWidth: "50px", padding: "10px 8px" }}
                            value={isRowEditing ? editingRow.promo_effect : activity.promo_effect}
                            isEditing={isRowEditing}
                            showControls={false}
                            onChange={(e) => updateEditingRow("promo_effect", e.target.value)}
                            renderValue={(v) => v?.toLocaleString("ru-RU")}
                          />
                        </td>
                        <td>{activity.promo_plan_value?.toLocaleString("ru-RU")}</td>
                        <td>
                          <CustomCheckbox
                            checked={isRowEditing ? !!editingRow.promo_is_active : !!activity.promo_is_active}
                            disabled={!isRowEditing}
                            onChange={(e) => updateEditingRow("promo_is_active", e.target.checked)}
                          />
                        </td>
                        <td className="promo-activity-table-edit">
                          <EditableCellActions
                            isEditing={isRowEditing}
                            onEdit={() => editActivity(activity)}
                            onConfirm={saveEditedRow}
                            onCancel={cancelRowEdit}
                          />
                        </td>
                      </tr>
                    );
                  })
                )
              }
              {/* <tr>
                            <td colSpan={10}>
                                <div style={{ display: "flex", alignItems: "center", gap: "8px"}}>
                                    {
                                        newRow ? (
                                        <>
                                            <button type="button" className="primary-button" onClick={handleSave} disabled={isSaving || !newRow.promo_name?.trim()}>
                                            <CheckIcon size={24} />
                                            Сохранить
                                            </button>
                                            <button type="button" className="cancel-button" onClick={() => setNewRow(null)} disabled={isSaving}>
                                            Отмена
                                            </button>
                                        </>
                                        ) : (
                                        <button type="button" className="primary-button" onClick={() => setNewRow({ ...EMPTY_ROW })}>
                                            <PlusIcon width={20} height={20} />
                                            Добавить активность
                                        </button>
                                        )
                                    }
                                </div>
                            </td>
                        </tr> */}
            </tbody>
          </table>
        </div>
        {/* </div> */}
      </div>
    </div>
  );
};

export default PromoCalendarModal;
